import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import VideoSection from '../components/VideoSection';
import '../styles/LandingPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [numResults, setNumResults] = useState(3);
  const [nlEmail, setNlEmail] = useState('');
  const [nlSent, setNlSent] = useState(false);
  const [nlSettings, setNlSettings] = useState(null);
  const [hasQuizProgress, setHasQuizProgress] = useState(false);

  useEffect(() => {
    fetchAlgorithmSettings();
    checkQuizProgress();
    // Load newsletter settings
    axios.get(`${API}/cta-settings`).then(r => setNlSettings(r.data)).catch(() => {});
  }, []);
  
  const checkQuizProgress = () => {
    const progress = localStorage.getItem('quiz_progress');
    setHasQuizProgress(!!progress);
  };
  
  const resumeQuiz = () => {
    const progress = JSON.parse(localStorage.getItem('quiz_progress'));
    navigate('/quiz', { state: { resumeProgress: progress } });
  };

  const fetchAlgorithmSettings = async () => {
    try {
      const response = await axios.get(`${API}/algorithm/settings`);
      setNumResults(response.data.num_results);
    } catch (error) {
      console.error("Error fetching algorithm settings:", error);
    }
  };

  return (
    <div className="landing-container" data-testid="landing-page">
      {/* Logo Header */}
     <div className="landing-logo">
        <a href="https://www.animoo.de" target="_blank" rel="noopener noreferrer">
          <img src="/animoo-logo.png" alt="animoo weddings" className="logo-image" />
        </a>
      </div>
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            {t('landing.title')}
          </h1>
          
          <p className="hero-subtitle" data-testid="hero-subtitle">
            {t('landing.subtitle')}
          </p>
          
          <div className="hero-buttons">
            {hasQuizProgress && (
              <button 
                className="btn-secondary"
                onClick={resumeQuiz}
                style={{marginRight: '16px'}}
              >
                {t('landing.resumeQuiz', language)}
              </button>
            )}
            <button 
              className="btn-primary"
              onClick={() => {
                localStorage.removeItem('quiz_progress');
                navigate('/quiz');
              }}
              data-testid="start-quiz-button"
            >
              {t('landing.startButton')}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">{t('landing.howItWorks')}</h2>
          
          <div className="features-grid">
            <div className="feature-card" data-testid="feature-1">
              <div className="feature-number">01</div>
              <h3>{t('landing.step1Title')}</h3>
              <p>{t('landing.step1Desc')}</p>
            </div>
            
            <div className="feature-card" data-testid="feature-2">
              <div className="feature-number">02</div>
              <h3>{t('landing.step2Title')}</h3>
              <p>{t('landing.step2Desc')}</p>
            </div>
            
            <div className="feature-card" data-testid="feature-3">
              <div className="feature-number">03</div>
              <h3>{t('landing.step3Title', { count: numResults })}</h3>
              <p>{t('landing.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <VideoSection />

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 data-testid="cta-title">{t('landing.ctaTitle')}</h2>
          <p>{t('landing.ctaDesc')}</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/quiz')}
            data-testid="cta-start-button"
          >
            {t('landing.ctaButton')}
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-newsletter">
          <p className="footer-nl-title">
            {language === 'de' ? (nlSettings?.newsletter_footer_de || 'Neue Destinationen & Angebote erhalten') 
             : language === 'en' ? (nlSettings?.newsletter_footer_en || 'Get new destinations & offers')
             : (nlSettings?.newsletter_footer_es || 'Recibe nuevos destinos y ofertas')}
          </p>
          {nlSent ? (
            <p className="footer-nl-done">
              {language === 'de' ? 'Angemeldet!' : language === 'en' ? 'Subscribed!' : 'Suscrito!'}
            </p>
          ) : (
            <form className="footer-nl-form" onSubmit={async (e) => {
              e.preventDefault();
              if (!nlEmail.includes('@')) return;
              try {
                await axios.post(`${API}/newsletter/subscribe`, { email: nlEmail, source: 'footer' });
                setNlSent(true);
              } catch (err) { console.error(err); }
            }}>
              <input
                type="email"
                placeholder="Email"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                className="footer-nl-input"
                data-testid="footer-newsletter-input"
              />
              <button type="submit" className="footer-nl-btn" data-testid="footer-newsletter-btn">
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
        <p>Wedding-O-Mat made by animoo weddings & developed by RealStory UG</p>
        <div className="footer-links">
          <Link to="/impressum">Impressum</Link>
          <Link to="/datenschutz">Datenschutz</Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
